/**
 * useAutoRefresh.js - Auto-Polling Hook for Stats
 * ================================================
 * Generic hook for automatically refreshing data at regular intervals.
 * Used for bot stats, call feeds, and other data that needs periodic updates.
 */

import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook for auto-refreshing data at regular intervals
 * @param {function} fetchFn - Async function to fetch data (should be memoized with useCallback)
 * @param {number} intervalMs - Interval in milliseconds (default: 10000 = 10 seconds)
 * @param {boolean} enabled - Whether auto-refresh is enabled (default: true)
 * @param {boolean} immediate - Whether to fetch immediately on mount (default: false)
 * @returns {Object} { refresh, isRefreshing }
 */
export function useAutoRefresh(fetchFn, intervalMs = 10000, enabled = true, immediate = false) {
    const intervalRef = useRef(null);
    const isRefreshingRef = useRef(false);

    /**
     * Execute the fetch function
     */
    const refresh = useCallback(async () => {
        if (isRefreshingRef.current) return;

        isRefreshingRef.current = true;
        try {
            await fetchFn();
        } catch (err) {
            console.error('[useAutoRefresh] Fetch error:', err);
        } finally {
            isRefreshingRef.current = false;
        }
    }, [fetchFn]);

    useEffect(() => {
        if (!enabled) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        // Fetch immediately if requested
        if (immediate) {
            refresh();
        }

        // Set up interval
        intervalRef.current = setInterval(() => {
            refresh();
        }, intervalMs);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [enabled, intervalMs, immediate, refresh]);

    return {
        refresh,
        isRefreshing: isRefreshingRef.current
    };
}

/**
 * Hook for auto-refreshing with incremental updates (e.g., new calls)
 * @param {function} fetchFn - Async function that accepts (lastId) and returns new items
 * @param {number} intervalMs - Interval in milliseconds
 * @param {boolean} enabled - Whether auto-refresh is enabled
 * @returns {Object} { items, latestId, refresh, addItems, clearItems }
 */
export function useIncrementalRefresh(fetchFn, intervalMs = 5000, enabled = true) {
    const intervalRef = useRef(null);
    const latestIdRef = useRef(null);

    /**
     * Fetch new items since last ID
     */
    const refresh = useCallback(async () => {
        try {
            const result = await fetchFn(latestIdRef.current);
            if (result?.latest_id) {
                latestIdRef.current = result.latest_id;
            }
            return result;
        } catch (err) {
            console.error('[useIncrementalRefresh] Fetch error:', err);
            return null;
        }
    }, [fetchFn]);

    /**
     * Set the latest ID (useful after initial load)
     */
    const setLatestId = useCallback((id) => {
        latestIdRef.current = id;
    }, []);

    /**
     * Clear the latest ID (to fetch all items again)
     */
    const resetLatestId = useCallback(() => {
        latestIdRef.current = null;
    }, []);

    useEffect(() => {
        if (!enabled) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        // Set up interval
        intervalRef.current = setInterval(() => {
            refresh();
        }, intervalMs);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [enabled, intervalMs, refresh]);

    return {
        refresh,
        setLatestId,
        resetLatestId,
        getLatestId: () => latestIdRef.current
    };
}

export default useAutoRefresh;
